import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { ArrowLeft, EyeOff, Lock, LogIn, RotateCw } from 'lucide-react';
import { PageLoading } from '../common/PageState';
import { EmptyState, ErrorState } from '../ui';
import { useAuth } from '../../contexts/AuthContext';
import { AuthRole, formatAuthRole } from '../../utils/roles';
import { sbg } from '../../theme/sbgTokens';
import { ACCESS_TAB_LOCATION } from '../../utils/settingsTabs';

/** A centered white card on the canvas that holds one guard state. */
const GuardCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      maxWidth: 560,
      mx: 'auto',
      mt: { xs: 2, sm: 6 },
      bgcolor: sbg.bg.surface,
      border: `1px solid ${sbg.border.default}`,
      borderRadius: sbg.radius.card,
    }}
  >
    {children}
  </Box>
);

/** "Admin and HR", "Admin, HR, Manager and Jr manager": who a page is for. */
const formatRoleList = (roles: readonly AuthRole[]) => {
  const labels = roles.map((role) => formatAuthRole(role));
  return labels.length <= 1 ? labels.join('') : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
};

/** The page every signed-in role can open: the same place "/" sends them. */
const homeFor = (role: AuthRole | null | undefined) =>
  role === 'employee' ? { path: '/org-chart', label: 'Go to org chart' } : { path: '/dashboard', label: 'Go to dashboard' };

const RequireRole: React.FC<{ roles: AuthRole[]; children: React.ReactNode }> = ({ roles, children }) => {
  const { profile, user, refreshProfile, loading, profileLoading, viewAsRole, actualRole, setViewAsRole } = useAuth();

  if (loading || profileLoading) {
    return <PageLoading label="Loading your workspace…" />;
  }

  if (!user) {
    return (
      <GuardCard>
        <EmptyState
          icon={<LogIn />}
          title="Sign in to continue"
          body="Your session has ended. Sign in again to open this page."
          action={
            <Button variant="contained" component={RouterLink} to="/login">
              Go to sign in
            </Button>
          }
        />
      </GuardCard>
    );
  }

  if (!profile) {
    return (
      <GuardCard>
        <ErrorState
          title="Your profile didn't load"
          body="You're signed in, but we couldn't load your profile, so we can't tell which pages you can use. If your role changed recently, refresh your profile."
          onRetry={refreshProfile}
          retryLabel="Refresh profile"
        />
      </GuardCard>
    );
  }

  if (!profile?.role || !roles.includes(profile.role)) {
    const home = homeFor(profile?.role);

    // An admin previewing another role lands here often; this is a preview
    // boundary, not a real denial, so the way out is to end the preview.
    if (viewAsRole && actualRole === 'admin') {
      const previewRole = formatAuthRole(viewAsRole);
      return (
        <GuardCard>
          <EmptyState
            icon={<EyeOff />}
            title={`${previewRole} can't open this page`}
            body={`You're previewing as ${previewRole}. This page is for ${formatRoleList(roles)}.`}
            secondaryAction={
              <Button variant="outlined" startIcon={<ArrowLeft />} component={RouterLink} to={home.path}>
                {home.label}
              </Button>
            }
            action={
              <Button variant="contained" onClick={() => setViewAsRole(null)}>
                Exit role preview
              </Button>
            }
          />
        </GuardCard>
      );
    }

    return (
      <GuardCard>
        <EmptyState
          icon={<Lock />}
          title="You don't have access to this page"
          body={
            <>
              Your role is {formatAuthRole(profile?.role)}, and this page needs a different one. Ask an admin to change
              your role in {ACCESS_TAB_LOCATION}.
            </>
          }
          secondaryAction={
            <Button variant="outlined" startIcon={<RotateCw />} onClick={refreshProfile}>
              Refresh role
            </Button>
          }
          action={
            <Button variant="contained" startIcon={<ArrowLeft />} component={RouterLink} to={home.path}>
              {home.label}
            </Button>
          }
        />
        <Typography
          variant="caption"
          component="p"
          sx={{ color: sbg.text.secondary, textAlign: 'center', px: 3, pb: 3, mt: -2 }}
        >
          Signed in as {user?.email || 'an unknown account'}
        </Typography>
      </GuardCard>
    );
  }

  return <>{children}</>;
};

export default RequireRole;
